import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { truckingBillAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
};

export default function FFBillingPrintPage() {
  const { id } = useParams();
  const [bill, setBill] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [minAmount, setMinAmount] = useState(250);

  useEffect(() => {
    truckingBillAPI.getById(id)
      .then((r) => {
        setBill(r.data.bill);
        setLines(r.data.lines || []);
        setMinAmount(r.data.bill?.minimumBillingAmount ?? 250);
      })
      .catch(() => setError('Failed to load billing statement.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!bill || loading) return;
    const prev = document.title;
    document.title = `BillingStatement-${bill.BillName || id}`;
    const t = setTimeout(() => window.print(), 300);
    return () => { clearTimeout(t); document.title = prev; };
  }, [bill, loading, id]);

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}>Loading…</div>;
  if (error) return <div style={{ padding: 32, textAlign: 'center', color: 'red' }}>{error}</div>;

  // Pull linked waybill data
  const waybill = bill.WayBillId || {};
  const customer = waybill.CustomerId || {};
  const customerName =
    customer.name           ||   // CustomerSearch stores as lowercase 'name'
    customer.CustomerName   ||   // Mongoose PascalCase (if schema uses it)
    waybill.CustomerName    ||   // denormalized PascalCase on waybill
    waybill.customerName    ||   // denormalized camelCase on waybill
    '—';
  const customerAddress = waybill.CustomerAddress || '—';
  const shipperName = waybill.Shipper || '—';
  const shipperAddress = waybill.ShipperAddress || '—';
  const serialNo = waybill.SerialNo || '—';
  const referenceNo  = bill.referenceNumber    || '';
  const billingNotes = bill.notes              || '';
  const waybillDesc  = bill.waybillDescription || waybill.Description || '';

  // Totals — minimum applied only to grand total
  let totalAmount = lines.reduce((s, l) => s + (l.SubTotal || 0), 0);
  if (totalAmount < minAmount) totalAmount = minAmount;
  const netOfVAT = totalAmount / 1.12;
  const vat = netOfVAT * 0.12;
  const withholding = netOfVAT * 0.02;
  const stripFee  = parseFloat(bill.StrippingFee)           || 0;
  const addlStrip = parseFloat(bill.additionalStrippingFee)  || 0;
  const totalFees = stripFee + addlStrip;
  const amountDue = bill.HasWithHolding
    ? totalAmount + totalFees - withholding
    : totalAmount + totalFees;
  const totalQty = lines.reduce((s, l) => s + (l.Quantity || 0), 0);

  const printFreight = bill.printFreight !== false;
  // colspan offsets matching the CSHTML logic
  const colspanTotalLabel = printFreight ? 5 : 4;
  const colspanSummaryLabel = printFreight ? 4 : 4;

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '11pt', padding: '24px', maxWidth: '920px', margin: '0 auto', background: '#fff' }}>
      <style>{`
        @media print {
          body { margin: 0; }
          @page { size: A4; margin: 12mm; }
        }
        table.bt { width: 100%; border-collapse: collapse; }
        table.bt td, table.bt th { border: 0; padding: 3px 6px; font-size: 10.5pt; }
        .bordered { border: 1px solid #000; padding: 6px 10px; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .row { display: flex; }
        .col-half { flex: 1; }
        .sig-row { display: flex; justify-content: space-between; margin-top: 28px; }
        .sig-col { flex: 1; }
        hr.thick { border: none; border-top: 1px solid #000; margin: 4px 0; }
        .void-watermark {
          position: fixed;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%) rotate(-45deg);
          font-size: 96pt; font-weight: 900;
          color: rgba(220, 38, 38, 0.18);
          pointer-events: none; z-index: 1000;
          user-select: none; white-space: nowrap; letter-spacing: 8px;
        }
        @media print { .void-watermark { position: fixed; } }
      `}</style>

      {bill.isVoided && <div className="void-watermark">VOID</div>}

      {/* Header */}
      <div className="text-center" style={{ marginBottom: 6 }}>
        <h2 style={{ margin: '0 0 2px', fontSize: '15pt' }}>KARYU CORPORATION</h2>
        <h4 style={{ margin: '0 0 2px', fontSize: '12pt', fontWeight: 'bold' }}>FREIGHT FORWARDING</h4>
        <p style={{ margin: '0 0 2px', fontSize: '9.5pt' }}>
          Q. Abeto St., Brgy., Abeto-Mirasol Taft South, Mandurriao, Iloilo City<br />
          Tel. No. (033) 337-1638 | Cell No. 0946 493 7897 | 0961 752 8993
        </p>
        <p style={{ margin: '0 0 8px', fontWeight: 'bold' }}>BILLING STATEMENT</p>
      </div>
      {bill.isVoided && (
        <div style={{
          background: '#fef2f2', border: '2px solid #dc2626',
          borderRadius: 6, padding: '8px 16px', marginBottom: 12,
          textAlign: 'center', color: '#dc2626', fontWeight: 700, fontSize: '11pt',
        }}>
          ⊘ VOIDED
          {bill.voidReason && ` — ${bill.voidReason}`}
          {bill.voidedAt && ` on ${new Date(bill.voidedAt).toLocaleDateString('en-PH')}`}
        </div>
      )}

      {/* Bill info box */}
      <div className="bordered" style={{ marginBottom: 8 }}>
        <div className="row">
          <div className="col-half">
            <div>Bill No.: <strong>{bill.BillName}</strong></div>
            {referenceNo && (
              <div>Reference No.: <strong>{referenceNo}</strong></div>
            )}
            <div><strong>System WB No.: {serialNo}</strong></div>
            <div><strong>Customer Name: {customerName}</strong></div>
            <div>Customer Address: {customerAddress}</div>
            {waybillDesc && (
              <div style={{ marginTop: 4 }}>
                <span style={{ color: '#555' }}>Description: </span>{waybillDesc}
              </div>
            )}
            {billingNotes && (
              <div style={{ marginTop: 4 }}>
                <span style={{ color: '#555' }}>Notes: </span>{billingNotes}
              </div>
            )}
          </div>
          <div className="col-half">
            <div>Billing Date: {fmt(bill.BillDate)}</div>
            <div>Due Date: {fmt(bill.BillDueDate)}</div>
            <div><strong>Shipper Name: {shipperName}</strong></div>
            <div>Shipper Address: {shipperAddress}</div>
          </div>
        </div>
      </div>

      {/* Line items table */}
      <div className="bordered" style={{ padding: 0 }}>
        <table className="bt" style={{ width: '100%' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ccc' }}>
              <th style={{ textAlign: 'left' }}>Description</th>
              <th>Width</th>
              <th>Height</th>
              <th>Length</th>
              <th className="text-right">Weight</th>
              {printFreight && <th className="text-right">Freight</th>}
              <th className="text-right">Quantity</th>
              <th className="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l._id}>
                <td>{l.Description}</td>
                <td>{l.Width ?? ''}</td>
                <td>{l.Height ?? ''}</td>
                <td>{l.Length ?? ''}</td>
                <td className="text-right">{l.Weight ?? ''}</td>
                {printFreight && <td className="text-right">{l.Freight != null ? PHP(l.Freight) : ''}</td>}
                <td className="text-right">{l.Quantity}</td>
                <td className="text-right">{totalAmount > minAmount ? PHP(l.SubTotal) : ' - '}</td>
              </tr>
            ))}

            {/* Total row */}
            <tr style={{ borderTop: '1px solid #000' }}>
              <td colSpan={colspanTotalLabel}></td>
              <td><strong>Total Amount</strong></td>
              <td className="text-right"><strong>{PHP(totalQty)}</strong></td>
              <td className="text-right"><strong>{PHP(totalAmount)}</strong></td>
            </tr>

            {/* Summary row */}
            <tr>
              <td colSpan={colspanSummaryLabel}></td>
              <td style={{ verticalAlign: 'top', lineHeight: 1.9, width: '120px', minWidth: '120px' }}>
                <div>NET of VAT:</div>
                <div>VAT:</div>
                {stripFee > 0 && <div>Stripping Fee:</div>}
                {addlStrip > 0 && <div>Add'l Stripping Fee:</div>}
                {bill.HasWithHolding && <div>Less: Withholding:</div>}
                <div><strong>Amount Due:</strong></div>
              </td>
              <td colSpan={4} className="text-right" style={{ verticalAlign: 'top', lineHeight: 1.9 }}>
                <div>{PHP(netOfVAT)}</div>
                <div>{PHP(vat)}</div>
                {stripFee > 0 && <div>{PHP(stripFee)}</div>}
                {addlStrip > 0 && <div>{PHP(addlStrip)}</div>}
                {bill.HasWithHolding && <div>{PHP(withholding)}</div>}
                <div><strong>{PHP(amountDue)}</strong></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Signatures */}
      <div className="sig-row">
        <div className="sig-col">
          <p style={{ margin: '0 0 4px' }}>Prepared By:</p>
          <p style={{ margin: '20px 0 4px' }}>____________________________</p>
          <p style={{ margin: 0 }}>Date: _______________________</p>
        </div>
        <div className="sig-col">
          <p style={{ margin: '0 0 4px' }}>Approved By:</p>
          <p style={{ margin: '20px 0 4px' }}>____________________________</p>
          <p style={{ margin: 0 }}>Date: _______________________</p>
        </div>
        <div className="sig-col">
          <p style={{ margin: '0 0 4px' }}>Received &amp; Checked By:</p>
          <p style={{ margin: '20px 0 4px' }}>Consignee:___________________</p>
          <p style={{ margin: 0 }}>Date: _______________________</p>
        </div>
      </div>

      <p style={{ textAlign: 'center', marginTop: 16, fontSize: '9.5pt', color: '#666' }}>
        {new Date().toLocaleString()}
      </p>
    </div>
  );
}
